import { useCallback, useEffect, useRef } from "react";
import type { McpPlaygroundMessage } from "./chatMessages";
import {
  CHAT_SCROLL_BOTTOM_THRESHOLD_PX,
  distanceFromScrollBottom,
  scrollChatToBottom,
  scrollChatToMessage
} from "./scrollChatToMessage";

type UseMcpPlaygroundChatScrollOptions = {
  messages: McpPlaygroundMessage[];
  isLoading: boolean;
  lastAssistantContent: string;
};

export function useMcpPlaygroundChatScroll({
  messages,
  isLoading,
  lastAssistantContent
}: UseMcpPlaygroundChatScrollOptions) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const latestAssistantMessageRef = useRef<HTMLElement | null>(null);
  const latestUserMessageRef = useRef<HTMLElement | null>(null);
  const thinkingStatusRef = useRef<HTMLOutputElement | null>(null);
  const userHasScrolledUpRef = useRef(false);

  const scrollToLatestActivity = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const scrollContainer = scrollRef.current;
      if (!scrollContainer) {
        return;
      }

      if (isLoading) {
        scrollChatToBottom(scrollContainer, behavior);
        return;
      }

      const anchor = latestAssistantMessageRef.current;
      if (anchor) {
        scrollChatToMessage(scrollContainer, anchor, behavior);
        return;
      }

      scrollChatToBottom(scrollContainer, behavior);
    },
    [isLoading]
  );

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      userHasScrolledUpRef.current = distanceFromScrollBottom(scrollContainer) > CHAT_SCROLL_BOTTOM_THRESHOLD_PX;
    };

    handleScroll();
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    const transcript = transcriptRef.current;
    if (!scrollContainer || !transcript) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const userJustSent = isLoading && lastMessage?.role === "user";

    const scrollIfAppropriate = () => {
      if (userJustSent) {
        scrollChatToBottom(scrollContainer, "smooth");
        userHasScrolledUpRef.current = false;
        return;
      }

      if (isLoading) {
        if (!userHasScrolledUpRef.current) {
          scrollChatToBottom(scrollContainer, "smooth");
        }
        return;
      }

      if (lastMessage?.role === "assistant") {
        if (!userHasScrolledUpRef.current) {
          scrollToLatestActivity("smooth");
        }
        return;
      }

      if (userHasScrolledUpRef.current) {
        return;
      }

      scrollChatToBottom(scrollContainer, "smooth");
    };

    scrollIfAppropriate();

    const observer = new ResizeObserver(scrollIfAppropriate);
    observer.observe(transcript);

    return () => observer.disconnect();
  }, [isLoading, lastAssistantContent, messages, scrollToLatestActivity]);

  return {
    scrollRef,
    transcriptRef,
    latestAssistantMessageRef,
    latestUserMessageRef,
    thinkingStatusRef
  };
}
